import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tour, TourBase, type TourStep } from '@deepguide-ai/ui/tour'
import { TOUR_DISABLED } from '@/lib/env'

/**
 * Guided-execution coach-marks (issue #1349).
 *
 * One reusable primitive on top of the shared `@deepguide-ai/ui/tour` engine,
 * adding the three things the raw engine lacks:
 *   1. a `when(state)` predicate per step (state-gated tours),
 *   2. per-user "seen" persistence (localStorage, workspace-scoped),
 *   3. a manual "Show me how" replay.
 *
 * The tour engine targets elements by global `document.querySelector`, so this
 * renders as a standalone overlay — it does NOT wrap page content, which avoids
 * remounting the page on open/close/replay. Anchor elements anywhere in the app
 * with `data-tour="<id>"`.
 *
 * Usage:
 *   const tour = useCoachTour({ tourId: 'nav-orientation', scopeKey: workspaceId, steps })
 *   ...
 *   <CoachTour controller={tour} />
 *   {tour.hasApplicableSteps && <button onClick={tour.replay}>Show me how</button>}
 */

export interface CoachStep extends TourStep {
  /**
   * State predicate. The step is included only when this returns `true` (or is
   * absent). Evaluated when the tour opens, so a step whose anchor/state does
   * not apply (e.g. the approve step for a non-reviewer) never appears.
   */
  when?: () => boolean
}

export interface CoachTourConfigOverrides {
  smoothScroll?: boolean
  stagePadding?: number
  stageRadius?: number
}

export interface UseCoachTourOptions {
  /** Stable id — used to build the localStorage "seen" key. */
  tourId: string
  steps: CoachStep[]
  /**
   * Extra scope appended to the seen key (e.g. workspaceId) so "seen" is
   * tracked per workspace, matching the repo's `screenata:*:${workspaceId}`
   * localStorage convention.
   */
  scopeKey?: string
  /**
   * When false, the tour never auto-opens (it stays replayable). Default true.
   */
  enabled?: boolean
  /** Override tour engine defaults (padding, scroll, etc.). */
  configOverrides?: CoachTourConfigOverrides
}

export interface CoachTourController {
  isOpen: boolean
  /** Open the tour from the first applicable step, ignoring "seen". */
  replay: () => void
  /** Close the tour and persist "seen". */
  close: () => void
  /** True when at least one step's `when()` currently applies. */
  hasApplicableSteps: boolean
  // Render inputs consumed by <CoachTour />.
  steps: CoachStep[]
  configOverrides?: CoachTourConfigOverrides
}

const SEEN_VALUE = 'true'

function seenStorageKey(tourId: string, scopeKey?: string): string {
  return `screenata:tour:${tourId}:seen${scopeKey ? `:${scopeKey}` : ''}`
}

function hasSeenTour(tourId: string, scopeKey?: string): boolean {
  if (typeof window === 'undefined') return true
  try {
    return (
      window.localStorage.getItem(seenStorageKey(tourId, scopeKey)) ===
      SEEN_VALUE
    )
  } catch {
    // Private mode / disabled storage — treat as seen so we never nag.
    return true
  }
}

function markTourSeen(tourId: string, scopeKey?: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(seenStorageKey(tourId, scopeKey), SEEN_VALUE)
  } catch {
    // no-op
  }
}

/**
 * A `when()` predicate that includes a step only if its `[data-tour="<key>"]`
 * anchor is currently in the DOM. Because a surface renders an action button
 * only when it's actually available (e.g. the test Approve button renders just
 * for a reviewer on a submitted test), anchor presence doubles as the state
 * gate — and guarantees an included step's element exists, so the engine never
 * stalls hunting for a missing anchor.
 */
export function domAnchorPresent(key: string): () => boolean {
  return () =>
    typeof document !== 'undefined' &&
    !!document.querySelector(`[data-tour="${key}"]`)
}

/** Apply per-step `when()` predicates and strip the predicate before rendering. */
export function applyStepPredicates(steps: CoachStep[]): TourStep[] {
  return steps
    .filter((step) => (step.when ? step.when() : true))
    .map(({ when: _when, ...rest }) => rest)
}

export function useCoachTour(
  options: UseCoachTourOptions
): CoachTourController {
  const { tourId, steps, scopeKey, enabled = true, configOverrides } = options

  const [isOpen, setIsOpen] = useState(false)

  // Signature of the currently-applicable steps — which anchors are in the DOM
  // right now.
  const [applicableKey, setApplicableKey] = useState('')
  const hasApplicableSteps = !TOUR_DISABLED && applicableKey.length > 0
  const autoOpenedRef = useRef(false)

  // Track the applicable-anchor set on a bounded, off-render poll, and auto-open
  // once it SETTLES (unchanged for ~600ms). A heavy page mounts its anchors
  // progressively over a second or more, so opening on the first anchor would
  // snapshot a one-step tour ("Next" = "Done"). Polling off the render cycle —
  // rather than a setState-per-commit effect — also avoids the infinite render
  // loop ("Maximum update depth exceeded") that ref-composing Radix children
  // turned a per-commit setState into. The interval self-terminates on settle.
  useEffect(() => {
    if (TOUR_DISABLED) return

    const computeKey = () =>
      steps
        .filter((step) => (step.when ? step.when() : true))
        .map((step) => String(step.element))
        .join('|')

    const INTERVAL = 200
    const STABLE_TICKS = 3 // set unchanged for ~600ms
    const MAX_ELAPSED = 6000

    let lastKey = computeKey()
    let stableTicks = 0
    let elapsed = 0
    setApplicableKey((prev) => (prev === lastKey ? prev : lastKey))

    const id = window.setInterval(() => {
      const key = computeKey()
      setApplicableKey((prev) => (prev === key ? prev : key))
      if (key === lastKey) stableTicks += 1
      else {
        lastKey = key
        stableTicks = 0
      }
      elapsed += INTERVAL

      if (stableTicks < STABLE_TICKS && elapsed < MAX_ELAPSED) return
      window.clearInterval(id)
      // Settled. Auto-open once, if enabled + unseen + something to show.
      if (!enabled || autoOpenedRef.current || key.length === 0) return
      autoOpenedRef.current = true
      if (!hasSeenTour(tourId, scopeKey)) setIsOpen(true)
    }, INTERVAL)

    return () => window.clearInterval(id)
  }, [enabled, steps, tourId, scopeKey])

  const replay = useCallback(() => setIsOpen(true), [])

  const close = useCallback(() => {
    setIsOpen(false)
    markTourSeen(tourId, scopeKey)
  }, [tourId, scopeKey])

  return useMemo(
    () => ({
      isOpen,
      replay,
      close,
      hasApplicableSteps,
      steps,
      configOverrides,
    }),
    [isOpen, replay, close, hasApplicableSteps, steps, configOverrides]
  )
}

export function CoachTour({ controller }: { controller: CoachTourController }) {
  const { isOpen, close, steps, configOverrides } = controller

  // Snapshot the applicable steps when we (re)open, so mid-tour state changes
  // don't reshuffle the running tour. `runId` forces the engine to remount and
  // restart from step 0 on each open/replay.
  const [runId, setRunId] = useState(0)
  const [activeSteps, setActiveSteps] = useState<TourStep[]>([])
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const next = applyStepPredicates(steps)
      if (next.length === 0) {
        // Nothing applies — bail out instead of opening an empty tour.
        close()
      } else {
        setActiveSteps(next)
        setRunId((id) => id + 1)
      }
    }
    wasOpenRef.current = isOpen
  }, [isOpen, steps, close])

  const handleOpenChange = useCallback(
    (nextOrUpdater: boolean | ((prev: boolean) => boolean)) => {
      const next =
        typeof nextOrUpdater === 'function'
          ? nextOrUpdater(isOpen)
          : nextOrUpdater
      if (!next) close()
    },
    [isOpen, close]
  )

  if (TOUR_DISABLED || !isOpen || activeSteps.length === 0) return null

  return (
    <Tour
      key={runId}
      open
      onOpenChange={handleOpenChange}
      config={{
        smoothScroll: true,
        // The engine expands the spotlight by stagePadding twice (element rect +
        // overlay/frame), so keep this small — a compact nav row / button needs
        // a tight hug, not a big loose box.
        stagePadding: 2,
        stageRadius: 6,
        ...configOverrides,
        steps: activeSteps as [TourStep, ...TourStep[]],
      }}
    >
      <TourBase />
    </Tour>
  )
}
